'use client';

import {
    BoldItalicUnderlineToggles,
    ListsToggle,
    addImportVisitor$,
    headingsPlugin,
    linkDialogPlugin,
    linkPlugin,
    listsPlugin,
    markdownShortcutPlugin,
    MDXEditor,
    realmPlugin,
    toolbarPlugin,
    UndoRedo,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { Listbox, Transition } from '@headlessui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createHeadingNode } from '@lexical/rich-text';
import { $patchStyleText, $setBlocksType } from '@lexical/selection';
import {
    $createParagraphNode,
    $createRangeSelection,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    $setSelection,
} from 'lexical';
import {
    DISPLAY_FONTS,
    TEXT_FONTS,
    sanitizeFontFamily,
} from '@/utils/google-fonts';
import { legacyInlineHtmlToMarkdown } from '@/utils/editableHtmlToMarkdown';

import {
    Fragment,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AllCapsButton } from './AllCapsButton';
import { ClearFormattingButton } from './ClearFormattingButton';
import { LinkButton } from './LinkButton';
import { TextColorButton } from './TextColorButton';
import { TextMarkerControl } from './TextMarkerControl';
import { useEditor } from '../context/EditorContext/EditorContext';
import { findNearestTextMatch } from '../helpers/textSelection';

// Exported so other editors can consume it
export const MARKDOWN_EDITOR_WHITELIST = [
    'Body',
    'Description',
    'DocumentContent',
    'Intro',
    'Header',
    'ServicesHeader',
    'Name',
    'Question',
    'Answer',
    'Tagline',
    'Role',
    'Title',
    'EmailLabel', // label text for email anchor (no link tools)
    'Number',
    'Numbers',
    'Label',
    'Address',
    'BusinessHour',
    'BannerText',
    'License',
    'Price',
];

/* ----------------------------- helpers ----------------------------- */

function leafOf(key) {
    const k = String(key ?? '');
    return (
        k
            .replace(/\[\d+\]$/, '')
            .split('.')
            .pop() || k
    );
}

// Fields where the link UI must be hidden/disabled (but keep markdown + color)
function isNoLinkLeaf(leaf) {
    const v = String(leaf || '').toLowerCase();
    return (
        v === 'emaillabel' || // hide link tool on label
        v === 'email' ||
        v === 'phone' ||
        v === 'number' ||
        v === 'numbers' ||
        v === 'tel' ||
        v.endsWith('phone') ||
        v.endsWith('phonenumber') ||
        v.endsWith('telephone')
    );
}

function supportsBlockStyleControls(componentName, fieldKey) {
    return (
        String(componentName || '') === 'TermsAndConditions' &&
        leafOf(fieldKey) === 'DocumentContent'
    );
}

function hasChildren(node) {
    return Boolean(node && Array.isArray(node.children));
}

function getInlineStyleAttribute(node) {
    if (
        !node ||
        node.type !== 'mdxJsxTextElement' ||
        !Array.isArray(node.attributes)
    ) {
        return '';
    }

    const styleAttr = node.attributes.find(
        (attr) =>
            attr?.type === 'mdxJsxAttribute' &&
            attr?.name === 'style' &&
            typeof attr?.value === 'string' &&
            attr.value.trim().length > 0
    );

    return styleAttr?.value || '';
}

function hasInlineStyle(node) {
    return Boolean(getInlineStyleAttribute(node));
}

function ensureBoldWeight(styleValue) {
    const clean = String(styleValue || '')
        .trim()
        .replace(/;+\s*$/, '');
    if (!clean) return 'font-weight: 700';
    if (/font-weight\s*:/i.test(clean)) return clean;
    return `${clean}; font-weight: 700`;
}

function normalizeStyledBoldMarkdown(markdown) {
    const input = String(markdown ?? '');
    if (!input.includes('<span') || !input.includes('**')) return input;

    let output = input;

    // MDXEditor exports styled bold as: <span style="...">**Text**</span>
    output = output.replace(
        /<span\s+style=(["'])(.*?)\1>(\s*)\*\*([\s\S]*?)\*\*(\s*)<\/span>/gi,
        (_, __quote, styleValue, leading, inner, trailing) =>
            `<span style="${ensureBoldWeight(
                styleValue
            )}">${leading}${inner}${trailing}</span>`
    );

    // Also normalize HTML-strong variant if present.
    output = output.replace(
        /<span\s+style=(["'])(.*?)\1>\s*<strong>([\s\S]*?)<\/strong>\s*<\/span>/gi,
        (_, __quote, styleValue, inner) =>
            `<span style="${ensureBoldWeight(styleValue)}">${inner}</span>`
    );

    return output;
}

const BUTTON_REGULAR_TEXT_CLASS = 'button-text-regular';

function hasExplicitBoldFormatting(markdown) {
    return /\*\*[^*]+\*\*|__[^_]+__|<strong\b|font-weight\s*:\s*[5-9]00/i.test(
        String(markdown ?? '')
    );
}

function hasButtonRegularFormatting(markdown) {
    return String(markdown ?? '').includes(BUTTON_REGULAR_TEXT_CLASS);
}

function removeButtonRegularFormatting(markdown) {
    return String(markdown ?? '').replace(
        /<span\s+class=(["'])button-text-regular\1>([\s\S]*?)<\/span>/gi,
        '$2'
    );
}

function defaultBoldMarkdown(markdown, defaultBold) {
    const normalized = normalizeStyledBoldMarkdown(markdown);
    if (
        !defaultBold ||
        !normalized.trim() ||
        hasExplicitBoldFormatting(normalized) ||
        hasButtonRegularFormatting(normalized)
    ) {
        return normalized;
    }
    return `**${normalized}**`;
}

function preserveButtonWeightChoice(markdown, defaultBold) {
    const normalized = normalizeStyledBoldMarkdown(markdown);
    if (defaultBold && hasExplicitBoldFormatting(normalized)) {
        return removeButtonRegularFormatting(normalized);
    }
    if (
        !defaultBold ||
        !normalized.trim() ||
        hasExplicitBoldFormatting(normalized) ||
        hasButtonRegularFormatting(normalized)
    ) {
        return normalized;
    }
    return `<span class="${BUTTON_REGULAR_TEXT_CLASS}">${normalized}</span>`;
}

const preserveNestedTextStyleImportPlugin = realmPlugin({
    init(realm) {
        realm.pub(addImportVisitor$, {
            priority: 100,
            testNode: (node) => Boolean(getInlineStyleAttribute(node)),
            visitNode: ({ mdastNode, actions }) => {
                const style = getInlineStyleAttribute(mdastNode);
                if (!style) {
                    actions.nextVisitor();
                    return;
                }

                // Preserve style context through nested formatting wrappers.
                // Required for structures like:
                // <span style="..."><strong>Text</strong></span>
                // <strong style="...">Text</strong>
                actions.addStyle(style, mdastNode);

                const stack = hasChildren(mdastNode)
                    ? [...mdastNode.children]
                    : [];
                while (stack.length > 0) {
                    const child = stack.pop();
                    if (!hasChildren(child)) continue;

                    if (hasInlineStyle(child)) {
                        // Nested styled elements keep their own style scope.
                        continue;
                    }

                    actions.addStyle(style, child);
                    stack.push(...child.children);
                }

                // Let the default visitor chain handle semantics (bold/italic/html).
                actions.nextVisitor();
            },
        });
    },
});

const BLOCK_STYLE_OPTIONS = [
    {
        label: 'Default',
        shortLabel: 'DEFAULT',
        title: 'Default',
        factory: () => $createParagraphNode(),
    },
    {
        label: 'Heading 2',
        shortLabel: 'H2',
        title: 'Heading 2',
        factory: () => $createHeadingNode('h2'),
    },
    {
        label: 'Heading 3',
        shortLabel: 'H3',
        title: 'Heading 3',
        factory: () => $createHeadingNode('h3'),
    },
    {
        label: 'Heading 4',
        shortLabel: 'H4',
        title: 'Heading 4',
        factory: () => $createHeadingNode('h4'),
    },
];

const HEADING_LEVEL_OPTIONS = [
    {
        value: 'p',
        label: 'Paragraph',
        factory: () => $createParagraphNode(),
    },
    ...Array.from({ length: 6 }, (_, index) => {
        const level = index + 1;
        return {
            value: `h${level}`,
            label: `H${level}`,
            factory: () => $createHeadingNode(`h${level}`),
        };
    }),
];

function getSelectionBlockInfo(editor) {
    let value = 'p';
    let text = '';

    editor.getEditorState().read(() => {
        const selection = $getSelection();
        const anchorNode = $isRangeSelection(selection)
            ? selection.anchor.getNode()
            : $getRoot().getFirstChild();
        if (!anchorNode) return;

        const block = anchorNode.getTopLevelElement?.() || anchorNode;
        text = block.getTextContent?.().trim() || '';

        if (block.getType?.() === 'heading') {
            value = block.getTag?.() || 'p';
        }
    });

    return { value, text };
}

function normalizeHeadingText(value) {
    return String(value || '')
        .replace(/\s+/g, ' ')
        .trim();
}

function getAccessibleDocuments() {
    if (typeof document === 'undefined') return [];

    const documents = [document];
    for (const frame of document.querySelectorAll('iframe')) {
        try {
            if (frame.contentDocument) documents.push(frame.contentDocument);
        } catch {
            // Cross-origin previews cannot be inspected for heading order.
        }
    }
    return documents;
}

function getRenderedHeadingValue(fieldKey, selectedText) {
    if (typeof document === 'undefined' || !fieldKey) return 'p';

    const normalizedText = normalizeHeadingText(selectedText);
    const fieldWrappers = getAccessibleDocuments()
        .flatMap((currentDocument) =>
            Array.from(
                currentDocument.querySelectorAll('[data-markdown-field-key]')
            )
        )
        .filter(
            (element) =>
                element.getAttribute('data-markdown-field-key') ===
                String(fieldKey)
        );
    const fieldWrapper =
        fieldWrappers.find(
            (element) =>
                normalizedText &&
                normalizeHeadingText(element.textContent) === normalizedText
        ) || fieldWrappers[0];
    const heading = fieldWrapper?.querySelector('h1, h2, h3, h4, h5, h6');

    return heading?.tagName?.toLowerCase() || 'p';
}

function getHeadingOrderWarning(fieldKey, selectedText, proposedLevel) {
    if (
        typeof document === 'undefined' ||
        !fieldKey ||
        !Number.isInteger(proposedLevel)
    ) {
        return '';
    }

    const documents = getAccessibleDocuments();
    const normalizedText = normalizeHeadingText(selectedText);
    const fieldWrappers = documents
        .flatMap((currentDocument) =>
            Array.from(
                currentDocument.querySelectorAll('[data-markdown-field-key]')
            )
        )
        .filter(
            (element) =>
                element.getAttribute('data-markdown-field-key') ===
                String(fieldKey)
        );
    const fieldWrapper =
        fieldWrappers.find(
            (element) =>
                normalizedText &&
                normalizeHeadingText(element.textContent) === normalizedText
        ) || fieldWrappers[0];
    const pageDocument =
        fieldWrapper?.ownerDocument ||
        documents.find((currentDocument) =>
            currentDocument.querySelector('main#main-content')
        ) ||
        documents.find((currentDocument) =>
            currentDocument.querySelector('main')
        );
    const pageRoot =
        fieldWrapper?.closest('main#main-content') ||
        pageDocument?.querySelector('main#main-content') ||
        pageDocument?.querySelector('main') ||
        pageDocument?.body;
    if (!pageRoot) return '';

    const headingSelector = 'h1, h2, h3, h4, h5, h6';
    const localHeadings = fieldWrapper
        ? Array.from(fieldWrapper.querySelectorAll(headingSelector))
        : [];
    let currentHeading =
        localHeadings.find(
            (heading) =>
                !normalizedText ||
                normalizeHeadingText(heading.textContent) === normalizedText
        ) || localHeadings[0];

    if (!currentHeading && !fieldWrapper && normalizedText) {
        currentHeading = Array.from(
            pageRoot.querySelectorAll(headingSelector)
        ).find(
            (heading) =>
                normalizeHeadingText(heading.textContent) === normalizedText
        );
    }
    const headings = Array.from(pageRoot.querySelectorAll(headingSelector));
    const currentIndex = currentHeading ? headings.indexOf(currentHeading) : -1;
    let previousHeading = currentIndex >= 0 ? headings[currentIndex - 1] : null;
    let nextHeading = currentIndex >= 0 ? headings[currentIndex + 1] : null;

    // A paragraph has no heading element yet. Locate its wrapper between the
    // existing headings so the proposed level can be validated immediately,
    // before the editor update reaches the preview.
    if (currentIndex < 0 && fieldWrapper) {
        const nodeApi = fieldWrapper.ownerDocument.defaultView?.Node;
        if (nodeApi) {
            for (const heading of headings) {
                const position = heading.compareDocumentPosition(fieldWrapper);
                if (position & nodeApi.DOCUMENT_POSITION_FOLLOWING) {
                    previousHeading = heading;
                    continue;
                }
                if (position & nodeApi.DOCUMENT_POSITION_PRECEDING) {
                    nextHeading = heading;
                    break;
                }
            }
        }
    }

    if (currentIndex < 0 && !fieldWrapper) return '';

    const previousLevel = previousHeading
        ? Number(previousHeading.tagName.slice(1))
        : null;
    const nextLevel = nextHeading ? Number(nextHeading.tagName.slice(1)) : null;
    const warnings = [];

    if (!previousLevel && proposedLevel > 1) {
        warnings.push(`H1 → H${proposedLevel}`);
    } else if (previousLevel && proposedLevel > previousLevel + 1) {
        warnings.push(`H${previousLevel} → H${proposedLevel}`);
    }
    if (nextLevel && nextLevel > proposedLevel + 1) {
        warnings.push(`H${proposedLevel} → H${nextLevel}`);
    }

    return warnings.length
        ? `Skipped heading level: ${warnings.join(' and ')}.`
        : '';
}

function BlockStyleButtons() {
    const [editor] = useLexicalComposerContext();
    const [selectedValue, setSelectedValue] = useState('');

    return (
        <select
            className="mdx-style-select"
            value={selectedValue}
            onChange={(event) => {
                const nextValue = event.target.value;
                setSelectedValue('');
                const option = BLOCK_STYLE_OPTIONS.find(
                    (item) => item.shortLabel === nextValue
                );
                if (!option) return;

                editor.update(() => {
                    const selection = $getSelection();
                    if (!$isRangeSelection(selection)) return;
                    $setBlocksType(selection, option.factory);
                });
                editor.focus();
            }}
            aria-label="Text styles"
            title="Text styles"
        >
            <option value="">Styles</option>
            {BLOCK_STYLE_OPTIONS.map((option) => (
                <option key={option.shortLabel} value={option.shortLabel}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}

function getFontFamilyOptions(siteEditor) {
    const configuredFonts = siteEditor?.global?.data?.fonts || {};
    const uploadedFonts = Array.isArray(siteEditor?.global?.data?.uploadedFonts)
        ? siteEditor.global.data.uploadedFonts
        : [];
    const candidates = [
        ...DISPLAY_FONTS,
        ...TEXT_FONTS,
        { font: configuredFonts.display },
        { font: configuredFonts.text },
        ...uploadedFonts.map((font) => ({ font: font?.family })),
    ];
    const seen = new Set();

    return candidates
        .map((entry) => sanitizeFontFamily(entry?.font))
        .filter((font) => {
            const key = font.toLowerCase();
            if (!font || seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .sort((left, right) => left.localeCompare(right));
}

function getSelectionFontFamily(editor) {
    let family = '';
    editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        for (const node of selection.getNodes()) {
            if (typeof node.getStyle !== 'function') continue;
            const match = node
                .getStyle()
                .match(/(?:^|;)\s*font-family\s*:\s*([^;]+)/i);
            if (match?.[1]) {
                family = sanitizeFontFamily(match[1].replace(/["']/g, ''));
                break;
            }
        }
    });
    return family;
}

const FONT_SIZE_OPTIONS = [
    8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72,
];

function getSelectionFontSize(editor) {
    let fontSize = '';
    editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        for (const node of selection.getNodes()) {
            if (typeof node.getStyle !== 'function') continue;
            const match = node
                .getStyle()
                .match(/(?:^|;)\s*font-size\s*:\s*([\d.]+)px(?:;|$)/i);
            if (match?.[1]) {
                fontSize = match[1];
                break;
            }
        }
    });
    return fontSize;
}

const FONT_WEIGHT_OPTIONS = [
    { value: '100', label: 'Thin' },
    { value: '200', label: 'Extra light' },
    { value: '300', label: 'Light' },
    { value: '400', label: 'Regular' },
    { value: '500', label: 'Medium' },
    { value: '600', label: 'Semibold' },
    { value: '700', label: 'Bold' },
    { value: '800', label: 'Extra bold' },
    { value: '900', label: 'Black' },
];

function getSelectionFontWeight(editor) {
    let fontWeight = '';
    editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        for (const node of selection.getNodes()) {
            if (typeof node.getStyle !== 'function') continue;
            const match = node
                .getStyle()
                .match(/(?:^|;)\s*font-weight\s*:\s*([\d.]+)(?:;|$)/i);
            if (match?.[1]) {
                fontWeight = match[1];
                break;
            }
        }
    });
    return fontWeight;
}

function getSelectionLineHeight(editor) {
    let value = '';
    editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        for (const node of selection.getNodes()) {
            const match = node
                .getStyle?.()
                ?.match(/(?:^|;)\s*--editor-line-height\s*:\s*([^;]+)/i);
            if (match?.[1]) {
                value = match[1].trim();
                break;
            }
        }
    });
    return value;
}

function expandSelectionToAllIfNone() {
    let selection = $getSelection();
    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        const root = $getRoot();
        root.select(0, root.getTextContentSize());
        selection = $getSelection();
    }
    return $isRangeSelection(selection) ? selection : null;
}

function restoreFrontendTextSelection(selectedText, preferredOffset = 0) {
    const text = String(selectedText || '');
    if (!text) return false;

    const textNodes = $getRoot().getAllTextNodes();
    const fullText = textNodes.map((node) => node.getTextContent()).join('');
    // Layout-only markup (including an existing marker) can make the DOM
    // offset differ slightly from Lexical's text offset. Pick the matching
    // occurrence nearest to the captured position instead of searching only
    // forward and accidentally restoring an older/later identical phrase.
    const start = findNearestTextMatch(fullText, text, preferredOffset);
    if (start < 0) return false;
    const end = start + text.length;
    let cursor = 0;
    let anchorNode = null;
    let anchorOffset = 0;
    let focusNode = null;
    let focusOffset = 0;

    for (const node of textNodes) {
        const length = node.getTextContentSize();
        if (!anchorNode && start <= cursor + length) {
            anchorNode = node;
            anchorOffset = Math.max(0, start - cursor);
        }
        if (end <= cursor + length) {
            focusNode = node;
            focusOffset = Math.max(0, end - cursor);
            break;
        }
        cursor += length;
    }

    if (!anchorNode || !focusNode) return false;
    const selection = $createRangeSelection();
    selection.setTextNodeRange(
        anchorNode,
        anchorOffset,
        focusNode,
        focusOffset
    );
    $setSelection(selection);
    return true;
}

function selectTextFromFrontend(editor, selectedText, preferredOffset = 0) {
    if (!editor?.update) return;
    editor.update(() => {
        restoreFrontendTextSelection(selectedText, preferredOffset);
    });
}

function mergeMarkerPreviewRects(rects) {
    return [...rects]
        .sort((a, b) => a.top - b.top || a.left - b.left)
        .reduce((lines, rect) => {
            const line = lines[lines.length - 1];
            const overlap = line
                ? Math.min(line.bottom, rect.bottom) -
                  Math.max(line.top, rect.top)
                : 0;

            if (!line || overlap <= Math.min(line.height, rect.height) * 0.5) {
                lines.push({
                    left: rect.left,
                    top: rect.top,
                    right: rect.right,
                    bottom: rect.bottom,
                    height: rect.height,
                });
                return lines;
            }

            line.left = Math.min(line.left, rect.left);
            line.top = Math.min(line.top, rect.top);
            line.right = Math.max(line.right, rect.right);
            line.bottom = Math.max(line.bottom, rect.bottom);
            line.height = line.bottom - line.top;
            return lines;
        }, []);
}

function renderGroupedMarkerPreviews(root) {
    const host =
        root.closest('[class*="_rootContentEditableWrapper_"]') ||
        root.parentElement;
    if (!host) return;

    host.classList.add('editor-marker-preview-host');
    host.querySelector(':scope > .editor-marker-preview-layer')?.remove();
    root.querySelectorAll('.editor-marker-preview-grouped').forEach((node) =>
        node.classList.remove('editor-marker-preview-grouped')
    );

    const groups = new Map();
    root.querySelectorAll('span[style*="--editor-marker"]').forEach(
        (node, index) => {
            const variant = node.style
                .getPropertyValue('--editor-marker')
                .trim();
            if (!['box', 'circle', 'line'].includes(variant)) return;
            const replay = node.style
                .getPropertyValue('--editor-marker-replay')
                .trim();
            // Older saved content has no replay id. Keep those spans
            // independent rather than accidentally joining unrelated effects.
            const key = replay
                ? `${variant}:${replay}`
                : `${variant}:single:${index}`;
            if (!groups.has(key)) groups.set(key, { variant, nodes: [] });
            groups.get(key).nodes.push(node);
        }
    );
    if (!groups.size) return;

    const hostRect = host.getBoundingClientRect();
    const layer = root.ownerDocument.createElement('div');
    layer.className = 'editor-marker-preview-layer';
    layer.setAttribute('aria-hidden', 'true');

    groups.forEach(({ variant, nodes }) => {
        const rects = nodes.flatMap((node) => {
            node.classList.add('editor-marker-preview-grouped');
            return Array.from(node.getClientRects()).filter(
                (rect) => rect.width > 0 && rect.height > 0
            );
        });
        if (!rects.length) return;

        const left = Math.min(...rects.map((rect) => rect.left));
        const top = Math.min(...rects.map((rect) => rect.top));
        const right = Math.max(...rects.map((rect) => rect.right));
        const bottom = Math.max(...rects.map((rect) => rect.bottom));
        const firstNode = nodes[0];
        const color =
            firstNode.style.getPropertyValue('--editor-marker-color') ||
            'currentColor';
        const gradient = firstNode.style.getPropertyValue(
            '--editor-marker-gradient'
        );
        const offsetY =
            Number.parseFloat(
                firstNode.style.getPropertyValue('--editor-marker-offset-y')
            ) || 0;

        if (variant === 'line') {
            mergeMarkerPreviewRects(rects).forEach((rect) => {
                const line = root.ownerDocument.createElement('span');
                line.className = 'editor-marker-preview-line';
                line.style.left = `${rect.left - hostRect.left}px`;
                line.style.top = `${rect.bottom - hostRect.top + offsetY}px`;
                line.style.width = `${rect.right - rect.left}px`;
                line.style.background = gradient || color;
                layer.appendChild(line);
            });
            return;
        }

        const outline = root.ownerDocument.createElement('span');
        outline.className = `editor-marker-preview-outline editor-marker-preview-${variant}`;
        outline.style.left = `${left - hostRect.left - 4}px`;
        outline.style.top = `${top - hostRect.top - 3 + offsetY}px`;
        outline.style.width = `${right - left + 8}px`;
        outline.style.height = `${bottom - top + 6}px`;
        outline.style.borderColor = color;
        layer.appendChild(outline);
    });

    host.appendChild(layer);
}

function LexicalEditorBridge({ lexicalEditorRef }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        lexicalEditorRef.current = editor;
        let frame = 0;
        let activeRoot = null;
        let resizeObserver = null;
        const schedule = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => {
                const root = editor.getRootElement();
                if (root) renderGroupedMarkerPreviews(root);
            });
        };
        const unbindRoot = editor.registerRootListener((root) => {
            resizeObserver?.disconnect();
            activeRoot = root;
            if (root) {
                resizeObserver = new ResizeObserver(schedule);
                resizeObserver.observe(root);
            }
            schedule();
        });
        const unregisterUpdate = editor.registerUpdateListener(schedule);
        window.addEventListener('resize', schedule);

        return () => {
            window.cancelAnimationFrame(frame);
            resizeObserver?.disconnect();
            unbindRoot();
            unregisterUpdate();
            window.removeEventListener('resize', schedule);
            if (activeRoot) {
                activeRoot
                    .querySelectorAll('.editor-marker-preview-grouped')
                    .forEach((node) =>
                        node.classList.remove('editor-marker-preview-grouped')
                    );
                const host =
                    activeRoot.closest(
                        '[class*="_rootContentEditableWrapper_"]'
                    ) || activeRoot.parentElement;
                host?.querySelector(
                    ':scope > .editor-marker-preview-layer'
                )?.remove();
                host?.classList.remove('editor-marker-preview-host');
            }
            if (lexicalEditorRef.current === editor) {
                lexicalEditorRef.current = null;
            }
        };
    }, [editor, lexicalEditorRef]);

    return null;
}

function FontFamilySelect({
    onApplied,
    onMarkdownSync,
    onRestoreSelection,
    open,
}) {
    const [editor] = useLexicalComposerContext();
    const siteEditor = useEditor();
    const options = useMemo(
        () => getFontFamilyOptions(siteEditor),
        [
            siteEditor?.global?.data?.fonts,
            siteEditor?.global?.data?.uploadedFonts,
        ]
    );
    const [selectedValue, setSelectedValue] = useState('');

    useEffect(() => {
        if (!open) return;
        setSelectedValue(getSelectionFontFamily(editor));
    }, [editor, open]);

    const orderedOptions = useMemo(() => {
        if (!selectedValue || !options.includes(selectedValue)) return options;
        return [
            selectedValue,
            ...options.filter((font) => font !== selectedValue),
        ];
    }, [options, selectedValue]);
    const activeDisplayFont =
        selectedValue ||
        sanitizeFontFamily(siteEditor?.global?.data?.fonts?.text) ||
        sanitizeFontFamily(siteEditor?.global?.data?.fonts?.display) ||
        'Default';

    const applyFont = (nextValue) => {
        setSelectedValue('');
        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const selection = expandSelectionToAllIfNone();
            if (!selection) return;
            $patchStyleText(selection, {
                'font-family': nextValue ? `"${nextValue}"` : null,
            });
        });
        editor.focus();
        onMarkdownSync?.();
        onApplied?.();
    };

    return (
        <Listbox value={selectedValue} onChange={applyFont}>
            <div className="relative w-[140px]">
                <Listbox.Button className="flex h-7 w-full items-center justify-between rounded-lg border border-neutral-200 bg-white px-2 text-left text-[11px] font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <span
                        className="truncate"
                        style={
                            activeDisplayFont !== 'Default'
                                ? {
                                      fontFamily: `"${activeDisplayFont}", sans-serif`,
                                  }
                                : undefined
                        }
                    >
                        {activeDisplayFont}
                    </span>
                    <span aria-hidden="true" className="ml-3 text-neutral-400">
                        ▾
                    </span>
                </Listbox.Button>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Listbox.Options className="absolute left-0 top-full z-50 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-xl focus:outline-none">
                        <Listbox.Option
                            value=""
                            className={({ active }) =>
                                `cursor-pointer px-2 py-1.5 text-[11px] ${
                                    active
                                        ? 'bg-primary-50 text-primary-700'
                                        : 'text-neutral-600'
                                }`
                            }
                        >
                            Default
                        </Listbox.Option>
                        {orderedOptions.map((font) => (
                            <Listbox.Option
                                key={font}
                                value={font}
                                className={({ active }) =>
                                    `cursor-pointer px-2 py-1.5 text-[11px] ${
                                        active
                                            ? 'bg-primary-50 text-primary-700'
                                            : 'text-neutral-800'
                                    }`
                                }
                            >
                                <span
                                    className="block truncate"
                                    style={{
                                        fontFamily: `"${font}", sans-serif`,
                                    }}
                                >
                                    {font}
                                </span>
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    );
}

function FontSizeSelect({
    onApplied,
    onMarkdownSync,
    onRestoreSelection,
    open,
}) {
    const [editor] = useLexicalComposerContext();
    const [sizeValue, setSizeValue] = useState('');

    useEffect(() => {
        if (!open) return;
        setSizeValue(getSelectionFontSize(editor));
    }, [editor, open]);

    const orderedOptions = useMemo(() => {
        const options = FONT_SIZE_OPTIONS.map(String);
        if (!sizeValue || options.includes(sizeValue)) return options;
        return [sizeValue, ...options];
    }, [sizeValue]);

    const applyFontSize = (nextValue) => {
        const shouldClear = !nextValue;
        setSizeValue(nextValue);

        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const selection = expandSelectionToAllIfNone();
            if (!selection) return;
            $patchStyleText(selection, {
                'font-size': shouldClear ? null : `${nextValue}px`,
                // Parent headings can use negative tracking calculated at their
                // larger font size. Reset it so smaller inline text does not
                // inherit a fixed spacing value that makes letters overlap.
                'letter-spacing': shouldClear ? null : 'normal',
            });
        });
        editor.focus();
        onMarkdownSync?.();
        onApplied?.();
    };

    return (
        <Listbox value={sizeValue} onChange={applyFontSize}>
            <div className="relative w-[66px]">
                <Listbox.Button
                    className="flex h-7 w-full items-center justify-between rounded-lg border border-neutral-200 bg-white px-2 text-left text-[11px] font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label="Font size"
                    title="Set font size"
                >
                    <span>{sizeValue ? `${sizeValue} px` : 'Size'}</span>
                    <span aria-hidden="true" className="ml-3 text-neutral-400">
                        ▾
                    </span>
                </Listbox.Button>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Listbox.Options className="absolute left-0 top-full z-50 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-xl focus:outline-none">
                        <Listbox.Option
                            value=""
                            className={({ active }) =>
                                `cursor-pointer px-2 py-1.5 text-[11px] ${
                                    active
                                        ? 'bg-primary-50 text-primary-700'
                                        : 'text-neutral-600'
                                }`
                            }
                        >
                            Default
                        </Listbox.Option>
                        {orderedOptions.map((size) => (
                            <Listbox.Option
                                key={size}
                                value={size}
                                className={({ active }) =>
                                    `cursor-pointer px-2 py-1.5 text-[11px] ${
                                        active
                                            ? 'bg-primary-50 text-primary-700'
                                            : 'text-neutral-800'
                                    }`
                                }
                            >
                                {size} px
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    );
}

function FontWeightSelect({
    onApplied,
    onMarkdownSync,
    onRestoreSelection,
    open,
}) {
    const [editor] = useLexicalComposerContext();
    const [weightValue, setWeightValue] = useState('');

    useEffect(() => {
        if (!open) return;
        setWeightValue(getSelectionFontWeight(editor));
    }, [editor, open]);

    const orderedOptions = useMemo(() => {
        if (
            !weightValue ||
            FONT_WEIGHT_OPTIONS.some((option) => option.value === weightValue)
        ) {
            return FONT_WEIGHT_OPTIONS;
        }
        return [
            { value: weightValue, label: weightValue },
            ...FONT_WEIGHT_OPTIONS,
        ];
    }, [weightValue]);

    const applyFontWeight = (nextValue) => {
        setWeightValue(nextValue);
        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const selection = expandSelectionToAllIfNone();
            if (!selection) return;
            $patchStyleText(selection, {
                'font-weight': nextValue || null,
            });
        });
        editor.focus();
        onMarkdownSync?.();
        onApplied?.();
    };

    const selectedOption = orderedOptions.find(
        (option) => option.value === weightValue
    );

    return (
        <Listbox value={weightValue} onChange={applyFontWeight}>
            <div className="relative w-[104px]">
                <Listbox.Button
                    className="flex h-7 w-full items-center justify-between rounded-lg border border-neutral-200 bg-white px-2 text-left text-[11px] font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label="Font weight"
                    title="Set font weight"
                >
                    <span>
                        {selectedOption
                            ? `${selectedOption.label} ${weightValue}`
                            : 'Weight'}
                    </span>
                    <span aria-hidden="true" className="ml-2 text-neutral-400">
                        ▾
                    </span>
                </Listbox.Button>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Listbox.Options className="absolute left-0 top-full z-50 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-xl focus:outline-none">
                        <Listbox.Option
                            value=""
                            className={({ active }) =>
                                `cursor-pointer px-2 py-1.5 text-[11px] ${
                                    active
                                        ? 'bg-primary-50 text-primary-700'
                                        : 'text-neutral-600'
                                }`
                            }
                        >
                            Default
                        </Listbox.Option>
                        {orderedOptions.map((option) => (
                            <Listbox.Option
                                key={option.value}
                                value={option.value}
                                className={({ active }) =>
                                    `cursor-pointer px-2 py-1.5 text-[11px] ${
                                        active
                                            ? 'bg-primary-50 text-primary-700'
                                            : 'text-neutral-800'
                                    }`
                                }
                            >
                                {option.label} {option.value}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    );
}

function LineHeightSelect({
    onApplied,
    onMarkdownSync,
    onRestoreSelection,
    open,
}) {
    const [editor] = useLexicalComposerContext();
    const [value, setValue] = useState('');
    const options = ['1', '1.15', '1.3', '1.5', '1.75', '2'];

    useEffect(() => {
        if (open) setValue(getSelectionLineHeight(editor));
    }, [editor, open]);

    const apply = (nextValue) => {
        setValue(nextValue);
        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const selection = expandSelectionToAllIfNone();
            if (!selection) return;
            $patchStyleText(selection, {
                '--editor-line-height': nextValue || null,
            });
        });
        editor.focus();
        onMarkdownSync?.();
        onApplied?.();
    };

    return (
        <Listbox value={value} onChange={apply}>
            <div className="relative w-[104px]">
                <Listbox.Button
                    className="flex h-7 w-full items-center justify-between rounded-lg border border-neutral-200 bg-white px-2 text-left text-[11px] font-medium text-neutral-700 shadow-sm"
                    aria-label="Line spacing"
                >
                    <span>{value ? `${value}×` : 'Line spacing'}</span>
                    <span aria-hidden="true" className="text-neutral-400">
                        ▾
                    </span>
                </Listbox.Button>
                <Listbox.Options className="absolute left-0 top-full z-50 mt-1 w-full rounded-lg border border-neutral-200 bg-white py-1 shadow-xl">
                    <Listbox.Option
                        value=""
                        className="cursor-pointer px-2 py-1.5 text-[11px]"
                    >
                        Default
                    </Listbox.Option>
                    {options.map((option) => (
                        <Listbox.Option
                            key={option}
                            value={option}
                            className="cursor-pointer px-2 py-1.5 text-[11px]"
                        >
                            {option}×
                        </Listbox.Option>
                    ))}
                </Listbox.Options>
            </div>
        </Listbox>
    );
}

function HeadingLevelSelect({
    fieldKey,
    onMarkdownSync,
    onRestoreSelection,
    open,
}) {
    const [editor] = useLexicalComposerContext();
    const [selectedValue, setSelectedValue] = useState('p');
    const [warning, setWarning] = useState('');

    useEffect(() => {
        if (!open) return;
        const block = getSelectionBlockInfo(editor);
        const currentValue =
            block.value === 'p'
                ? getRenderedHeadingValue(fieldKey, block.text)
                : block.value;
        setSelectedValue(currentValue);
        const level = /^h[1-6]$/.test(currentValue)
            ? Number(currentValue.slice(1))
            : null;
        setWarning(getHeadingOrderWarning(fieldKey, block.text, level));
    }, [editor, fieldKey, open]);

    const applyHeadingLevel = (nextValue) => {
        const option = HEADING_LEVEL_OPTIONS.find(
            (item) => item.value === nextValue
        );
        if (!option) return;

        const { text } = getSelectionBlockInfo(editor);
        setSelectedValue(nextValue);
        setWarning(
            getHeadingOrderWarning(
                fieldKey,
                text,
                nextValue === 'p' ? null : Number(nextValue.slice(1))
            )
        );

        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;
            $setBlocksType(selection, option.factory);
        });
        editor.focus();
        onMarkdownSync?.();
    };

    const selectedOption =
        HEADING_LEVEL_OPTIONS.find(
            (option) => option.value === selectedValue
        ) || HEADING_LEVEL_OPTIONS[0];

    return (
        <div className="w-[72px]">
            <Listbox value={selectedValue} onChange={applyHeadingLevel}>
                <div className="relative">
                    <Listbox.Button
                        className={`flex h-7 w-full items-center justify-between rounded-lg border bg-white px-2 text-left text-[11px] font-medium shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            warning
                                ? 'border-amber-400 text-amber-800'
                                : 'border-neutral-200 text-neutral-700'
                        }`}
                        aria-label="Heading level"
                        title="Set heading level"
                    >
                        <span>
                            {selectedValue === 'p'
                                ? 'Style'
                                : selectedOption.label}
                        </span>
                        <span className="ml-3 flex items-center gap-2">
                            {warning && (
                                <span
                                    aria-label={warning}
                                    className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 font-bold text-amber-700"
                                    role="status"
                                    title={warning}
                                >
                                    !
                                </span>
                            )}
                            <span
                                aria-hidden="true"
                                className="text-neutral-400"
                            >
                                ▾
                            </span>
                        </span>
                    </Listbox.Button>
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Listbox.Options className="absolute left-0 top-full z-50 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-xl focus:outline-none">
                            {HEADING_LEVEL_OPTIONS.map((option) => (
                                <Listbox.Option
                                    key={option.value}
                                    value={option.value}
                                    className={({ active }) =>
                                        `cursor-pointer px-2 py-1.5 text-[11px] ${
                                            active
                                                ? 'bg-primary-50 text-primary-700'
                                                : 'text-neutral-800'
                                        }`
                                    }
                                >
                                    {option.label}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </Transition>
                </div>
            </Listbox>
        </div>
    );
}

function FontFamilyButton({
    fieldKey,
    panelHostRef,
    open,
    onRequestToggle,
    onRequestClose,
    onMarkdownSync,
    onRestoreSelection,
}) {
    return (
        <>
            <button
                type="button"
                className="mdx-btn"
                data-active={open ? 'true' : undefined}
                title="Typography"
                aria-label="Typography"
                onMouseDown={(event) => {
                    event.preventDefault();
                    onRequestToggle?.();
                }}
            >
                <svg
                    aria-hidden="true"
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M4 5h16" />
                    <path d="M12 5v14" />
                    <path d="M8 19h8" />
                </svg>
            </button>

            {panelHostRef?.current
                ? createPortal(
                      <Transition
                          show={open}
                          appear
                          enter="transition-all ease-out duration-200"
                          enterFrom="opacity-0 -translate-y-2 max-h-0"
                          enterTo="opacity-100 translate-y-0 max-h-72"
                          leave="transition-all ease-in duration-150"
                          leaveFrom="opacity-100 translate-y-0 max-h-72"
                          leaveTo="opacity-0 -translate-y-2 max-h-0"
                      >
                          <div className="mdx-panel-block">
                              <div>
                                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                                      Typography
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2">
                                      <HeadingLevelSelect
                                          fieldKey={fieldKey}
                                          open={open}
                                          onMarkdownSync={onMarkdownSync}
                                          onRestoreSelection={
                                              onRestoreSelection
                                          }
                                      />
                                      <FontFamilySelect
                                          open={open}
                                          onMarkdownSync={onMarkdownSync}
                                          onRestoreSelection={
                                              onRestoreSelection
                                          }
                                          onApplied={() => onRequestClose?.()}
                                      />
                                      <FontWeightSelect
                                          open={open}
                                          onMarkdownSync={onMarkdownSync}
                                          onRestoreSelection={
                                              onRestoreSelection
                                          }
                                          onApplied={() => onRequestClose?.()}
                                      />
                                      <FontSizeSelect
                                          open={open}
                                          onMarkdownSync={onMarkdownSync}
                                          onRestoreSelection={
                                              onRestoreSelection
                                          }
                                          onApplied={() => onRequestClose?.()}
                                      />
                                      <LineHeightSelect
                                          open={open}
                                          onMarkdownSync={onMarkdownSync}
                                          onRestoreSelection={
                                              onRestoreSelection
                                          }
                                          onApplied={() => onRequestClose?.()}
                                      />
                                  </div>
                              </div>
                          </div>
                      </Transition>,
                      panelHostRef.current
                  )
                : null}
        </>
    );
}

/* ------------------------------ component ------------------------------ */

export function MDEditor(props) {
    const {
        value = '',
        onChange,
        linkValue, // schema-provided destination for this field
        onLinkValueChange,
        hideLinkButton = false,
        disableLinking = false,
        compactToolbar = false,
        defaultBold = false,
        fieldKey, // used to detect Email/Phone/EmailLabel fields
        componentName,
        deferExternalSyncWhileFocused = false,
    } = props;

    const externalMarkdown = useMemo(
        () =>
            defaultBoldMarkdown(legacyInlineHtmlToMarkdown(value), defaultBold),
        [defaultBold, value]
    );
    const editorFocusedRef = useRef(false);
    const [deferredMarkdown, setDeferredMarkdown] = useState(externalMarkdown);

    useEffect(() => {
        if (deferExternalSyncWhileFocused && editorFocusedRef.current) return;
        setDeferredMarkdown(externalMarkdown);
    }, [deferExternalSyncWhileFocused, externalMarkdown]);

    useEffect(() => {
        if (!defaultBold) return;

        const storedMarkdown = normalizeStyledBoldMarkdown(
            legacyInlineHtmlToMarkdown(value)
        );
        if (
            !storedMarkdown.trim() ||
            hasExplicitBoldFormatting(storedMarkdown) ||
            hasButtonRegularFormatting(storedMarkdown)
        ) {
            return;
        }
        onChange(externalMarkdown);
    }, [defaultBold, externalMarkdown, onChange, value]);

    const normalizedMarkdown = deferExternalSyncWhileFocused
        ? deferredMarkdown
        : externalMarkdown;

    // schema link present?
    const hasSchemaLink = useMemo(
        () => Boolean(linkValue && String(linkValue).trim()),
        [linkValue]
    );
    const hasExternalLinkControl = typeof onLinkValueChange === 'function';

    // Email/Phone/EmailLabel detection (disable linking but keep markdown & color)
    const isNoLink = useMemo(() => isNoLinkLeaf(leafOf(fieldKey)), [fieldKey]);

    // Effective flags
    const EFFECTIVE_DISABLE_LINKING =
        Boolean(disableLinking) ||
        hasSchemaLink ||
        hasExternalLinkControl ||
        isNoLink;
    const EFFECTIVE_HIDE_LINK_BTN =
        Boolean(hideLinkButton) ||
        (hasSchemaLink && !hasExternalLinkControl) ||
        isNoLink;
    const SHOW_BLOCK_STYLE_CONTROLS = useMemo(
        () => supportsBlockStyleControls(componentName, fieldKey),
        [componentName, fieldKey]
    );
    const SHOW_TYPOGRAPHY_CONTROLS = !compactToolbar;
    const SHOW_LIST_CONTROLS = !compactToolbar;
    const SHOW_COLOR_AND_EFFECT_CONTROLS = !compactToolbar;
    // refs/state
    const linkPanelHostRef = useRef(null);
    const colorPanelHostRef = useRef(null);
    const fontPanelHostRef = useRef(null);
    const panelsWrapperRef = useRef(null);
    const editorRef = useRef(null);
    const lexicalEditorRef = useRef(null);
    const frontendSelectionRef = useRef(null);
    const [openPanel, setOpenPanel] = useState(
        /** @type {null|'link'|'color'|'font'} */ (null)
    );
    const [queuedPanel, setQueuedPanel] = useState(
        /** @type {null|'link'|'color'|'font'} */ (null)
    );
    const TRANSITION_MS = 200;

    const handleChange = (nextVal) => {
        // Persist immediately so save/deploy never misses the latest edit.
        onChange(preserveButtonWeightChoice(String(nextVal), defaultBold));
    };

    const syncMarkdownFromEditor = useCallback(() => {
        if (typeof window === 'undefined') return;
        window.requestAnimationFrame(() => {
            const editor = editorRef.current;
            if (!editor?.getMarkdown) return;
            onChange(
                preserveButtonWeightChoice(
                    String(editor.getMarkdown() || ''),
                    defaultBold
                )
            );
        });
    }, [defaultBold, onChange]);

    const restoreFrontendSelection = useCallback(() => {
        const selection = frontendSelectionRef.current;
        if (!selection) return null;

        // A selection forwarded from the rendered preview is a one-shot
        // hand-off to Lexical. Once restored, Lexical owns the active range.
        // Keeping the forwarded range cached made a later toolbar click jump
        // back to the previous words after an effect was removed.
        frontendSelectionRef.current = null;
        return restoreFrontendTextSelection(selection.text, selection.start);
    }, []);

    useEffect(() => {
        const handleFrontendSelection = (event) => {
            const detail = event?.detail;
            if (!detail || String(detail.fieldKey) !== String(fieldKey)) return;
            frontendSelectionRef.current = {
                text: String(detail.text || ''),
                start: Number(detail.start) || 0,
            };
            selectTextFromFrontend(
                lexicalEditorRef.current,
                detail.text,
                detail.start
            );
        };
        window.addEventListener(
            'editor:frontend-text-selection',
            handleFrontendSelection
        );
        return () =>
            window.removeEventListener(
                'editor:frontend-text-selection',
                handleFrontendSelection
            );
    }, [fieldKey]);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;

        const currentMarkdown = normalizeStyledBoldMarkdown(
            editor.getMarkdown?.() || ''
        );

        if (currentMarkdown === normalizedMarkdown) return;
        editor.setMarkdown(normalizedMarkdown);
    }, [normalizedMarkdown]);

    // animated panel host
    useLayoutEffect(() => {
        const wrapper = panelsWrapperRef.current;
        if (!wrapper) return;

        // Portal children can mount after this layout effect. Keep the open
        // wrapper at its natural height so a late-mounted or resized panel
        // always participates in layout and pushes the editor field down.
        if (openPanel) {
            wrapper.style.transition = '';
            wrapper.style.height = 'auto';
            wrapper.style.overflow = 'visible';
            return;
        }

        const start = wrapper.offsetHeight;
        wrapper.style.height = `${start}px`;
        wrapper.style.overflow = 'hidden';
        // force reflow
        // eslint-disable-next-line no-unused-expressions
        wrapper.offsetHeight;

        wrapper.style.transition = `height ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
        wrapper.style.height = '0px';

        const onEnd = (event) => {
            if (event.target !== wrapper || event.propertyName !== 'height') {
                return;
            }
            wrapper.style.transition = '';
            wrapper.style.height = '0px';
            wrapper.style.overflow = 'hidden';
            wrapper.removeEventListener('transitionend', onEnd);

            if (queuedPanel) {
                const toOpen = queuedPanel;
                setQueuedPanel(null);
                requestAnimationFrame(() => setOpenPanel(toOpen));
            }
        };

        wrapper.addEventListener('transitionend', onEnd);

        // A zero-height close has no transitionend event. Use the same cleanup
        // as a fallback so switching panels cannot get stuck.
        const fallback = window.setTimeout(onEnd, TRANSITION_MS + 50, {
            target: wrapper,
            propertyName: 'height',
        });
        return () => {
            window.clearTimeout(fallback);
            wrapper.removeEventListener('transitionend', onEnd);
        };
    }, [openPanel, queuedPanel]);

    const requestToggle = (kind /* 'link' | 'color' | 'font' */) => {
        if (openPanel === kind) {
            setQueuedPanel(null);
            setOpenPanel(null);
        } else if (openPanel == null) {
            setOpenPanel(kind);
        } else {
            setQueuedPanel(kind);
            setOpenPanel(null);
        }
    };

    const requestClose = (kind /* 'link' | 'color' | 'font' */) => {
        if (openPanel === kind) {
            setQueuedPanel(null);
            setOpenPanel(null);
        }
    };

    // Plugins: drop link plugins entirely when linking is disabled
    const plugins = [
        preserveNestedTextStyleImportPlugin(),
        ...(EFFECTIVE_DISABLE_LINKING
            ? []
            : [linkPlugin(), linkDialogPlugin()]),
        headingsPlugin(),
        listsPlugin(),
        markdownShortcutPlugin(),
        toolbarPlugin({
            toolbarContents: () => (
                <div className="w-full">
                    <div
                        className="mdx-toolbar-row mb-[2px] flex items-center"
                        onMouseDownCapture={() => {
                            if (!frontendSelectionRef.current) return;
                            lexicalEditorRef.current?.update(() => {
                                restoreFrontendSelection();
                            });
                        }}
                    >
                        <LexicalEditorBridge
                            lexicalEditorRef={lexicalEditorRef}
                        />
                        <UndoRedo />
                        {!EFFECTIVE_HIDE_LINK_BTN &&
                            (!EFFECTIVE_DISABLE_LINKING ||
                                hasExternalLinkControl) && (
                                <LinkButton
                                    panelHostRef={linkPanelHostRef}
                                    open={openPanel === 'link'}
                                    onRequestToggle={() =>
                                        requestToggle('link')
                                    }
                                    onRequestClose={() => requestClose('link')}
                                    visible
                                    enabled
                                    onMarkdownSync={syncMarkdownFromEditor}
                                    onRestoreSelection={
                                        restoreFrontendSelection
                                    }
                                    externalValue={linkValue}
                                    onExternalChange={onLinkValueChange}
                                />
                            )}
                        {SHOW_BLOCK_STYLE_CONTROLS && <BlockStyleButtons />}
                        {SHOW_TYPOGRAPHY_CONTROLS && (
                            <FontFamilyButton
                                fieldKey={fieldKey}
                                panelHostRef={fontPanelHostRef}
                                open={openPanel === 'font'}
                                onRequestToggle={() => requestToggle('font')}
                                onRequestClose={() => requestClose('font')}
                                onMarkdownSync={syncMarkdownFromEditor}
                                onRestoreSelection={restoreFrontendSelection}
                            />
                        )}
                        <span
                            className="mdx-toolbar-spacer"
                            aria-hidden="true"
                        />
                        <div className="mdx-inline-format-group">
                            <BoldItalicUnderlineToggles
                                options={['Bold', 'Italic', 'Underline']}
                            />
                        </div>
                        {SHOW_LIST_CONTROLS && (
                            <ListsToggle options={['bullet', 'number']} />
                        )}
                        <AllCapsButton />

                        {SHOW_COLOR_AND_EFFECT_CONTROLS && (
                            <>
                                <span
                                    className="mdx-toolbar-spacer"
                                    aria-hidden="true"
                                />
                                <TextColorButton
                                    panelHostRef={colorPanelHostRef}
                                    open={openPanel === 'color'}
                                    onRequestToggle={() =>
                                        requestToggle('color')
                                    }
                                    onMarkdownSync={syncMarkdownFromEditor}
                                    onRestoreSelection={
                                        restoreFrontendSelection
                                    }
                                    panelAfter={({ angle, setAngle }) => (
                                        <TextMarkerControl
                                            open={openPanel === 'color'}
                                            onMarkdownSync={
                                                syncMarkdownFromEditor
                                            }
                                            onRestoreSelection={
                                                restoreFrontendSelection
                                            }
                                            sharedAngle={angle}
                                            onSharedAngleChange={setAngle}
                                        />
                                    )}
                                />
                            </>
                        )}

                        <span
                            className="mdx-toolbar-spacer"
                            aria-hidden="true"
                        />
                        <ClearFormattingButton
                            onMarkdownSync={syncMarkdownFromEditor}
                            onRestoreSelection={restoreFrontendSelection}
                        />
                    </div>

                    {/* Panel host(s) */}
                    <div
                        ref={panelsWrapperRef}
                        className="mdx-panel-surface"
                        style={{ height: 0, overflow: 'hidden' }}
                    >
                        {(!EFFECTIVE_DISABLE_LINKING ||
                            hasExternalLinkControl) && (
                            <div ref={linkPanelHostRef} />
                        )}
                        {SHOW_COLOR_AND_EFFECT_CONTROLS && (
                            <div ref={colorPanelHostRef} />
                        )}
                        {SHOW_TYPOGRAPHY_CONTROLS && (
                            <div ref={fontPanelHostRef} />
                        )}
                    </div>
                </div>
            ),
        }),
    ];

    // Block Cmd/Ctrl+K when linking is disabled (email/phone/label or schema)
    const handleKeyDownCapture = (e) => {
        if (!EFFECTIVE_DISABLE_LINKING) return;
        const isMac =
            typeof navigator !== 'undefined' &&
            /Mac|iPhone|iPad/.test(navigator.platform);
        const meta = isMac ? e.metaKey : e.ctrlKey;
        if (meta && (e.key === 'k' || e.key === 'K')) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const handleFocusCapture = () => {
        editorFocusedRef.current = true;
    };

    const handlePointerDownCapture = (event) => {
        if (
            event.target instanceof Element &&
            event.target.closest(
                '[contenteditable="true"], .mdx-content, .mdxeditor-root-contenteditable'
            )
        ) {
            // A direct editor-field selection supersedes the last selection
            // forwarded from the frontend preview. MDXEditor uses the
            // generated `_contentEditable_*` class plus `.mdx-content`, not
            // `.mdxeditor-root-contenteditable`, on the editable node.
            frontendSelectionRef.current = null;
        }
    };

    const handleBlurCapture = (event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        editorFocusedRef.current = false;
        if (deferExternalSyncWhileFocused) {
            setDeferredMarkdown(externalMarkdown);
        }
    };

    return (
        <div
            className={`mdx-editor-shell relative rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600 ${
                openPanel ? 'z-[100]' : 'z-0'
            }`}
            data-editor-flags={`${EFFECTIVE_HIDE_LINK_BTN ? 'hide-link' : ''} ${
                EFFECTIVE_DISABLE_LINKING ? 'no-linking' : ''
            } ${isNoLink ? 'no-link-leaf' : ''}`}
            onKeyDownCapture={handleKeyDownCapture}
            onPointerDownCapture={handlePointerDownCapture}
            onFocusCapture={handleFocusCapture}
            onBlurCapture={handleBlurCapture}
        >
            <div
                className={`mdxeditor-overrides ${
                    openPanel ? 'mdx-panel-open' : ''
                }`}
            >
                <MDXEditor
                    ref={editorRef}
                    className="block w-full rounded-sm bg-white text-xs text-gray-900 md:text-sm"
                    contentEditableClassName="mdx-content"
                    markdown={normalizedMarkdown}
                    onChange={handleChange}
                    plugins={plugins}
                />
            </div>

            {/* Scoped styling fixes */}
            <style>{`
.mdxeditor-overrides .mdx-content,
.mdxeditor-overrides [contenteditable="true"] {
  caret-color: #111827 !important;
}

.mdxeditor-overrides .mdx-content {
  min-height: 2.75rem;
}

/* More than one field can keep its typography panel expanded. Lift the field
   that currently owns keyboard focus above the other expanded editors so its
   Listbox menus cannot be covered by a later field's toolbar. */
.mdx-editor-shell:focus-within {
  z-index: 110 !important;
}

/* Text sizing and line spacing are frontend overrides; keep the editing field
   at its normal readable scale while preserving the serialized styles. */
.mdxeditor-overrides .mdx-content [style*="font-size"],
.mdxeditor-overrides .mdx-content [style*="--editor-line-height"] {
  font-size: inherit !important;
  line-height: inherit !important;
}

/* Let formatting dropdowns float over the editable field. MDXEditor sets
   overflow-x: auto on its toolbar, which otherwise clips vertical menus. */
.mdxeditor-overrides [class*="_toolbarRoot_"] {
  overflow: visible !important;
  z-index: 30 !important;
}

/* A sticky toolbar is visually displaced from its reserved layout space.
   Expanded panels must remain in normal flow so the editable field cannot
   scroll underneath them. */
.mdxeditor-overrides.mdx-panel-open [class*="_toolbarRoot_"] {
  position: static !important;
  top: auto !important;
}

.mdxeditor-overrides .mdx-panel-surface {
  position: relative;
  z-index: 40;
}

.mdxeditor-overrides .mdx-panel-block {
  position: relative;
  overflow: visible !important;
  z-index: 40;
}

.mdxeditor-overrides [class*="_rootContentEditableWrapper_"] {
  z-index: 0;
}

/* Default link look: don't force underline or bold; let markdown decide */
.mdxeditor-overrides .mdx-content a,
.mdxeditor-overrides a.mdxeditor-link,
.mdxeditor-overrides [data-lexical-link="true"],
.mdxeditor-overrides [contenteditable="true"] a {
  color: var(--primary-700, #1d4ed8) !important;
  text-decoration: none !important;
  font-weight: inherit !important;
}

/* Hover: still no forced underline; underline only if user applied it */
.mdxeditor-overrides .mdx-content a:hover,
.mdxeditor-overrides a.mdxeditor-link:hover,
.mdxeditor-overrides [data-lexical-link="true"]:hover,
.mdxeditor-overrides [contenteditable="true"] a:hover {
  text-decoration: none !important;
  font-weight: inherit !important;
}

.mdxeditor-overrides .mdx-content h1,
.mdxeditor-overrides .mdx-content h2,
.mdxeditor-overrides .mdx-content h3,
.mdxeditor-overrides .mdx-content h4,
.mdxeditor-overrides .mdx-content h5,
.mdxeditor-overrides .mdx-content h6 {
  color: #111827 !important;
  font-family: inherit !important;
  font-size: inherit !important;
  font-weight: inherit !important;
  line-height: inherit !important;
  margin: 0 !important;
}

.mdxeditor-overrides .mdx-content ul,
.mdxeditor-overrides .mdx-content ol {
  margin: 0.75rem 0 !important;
  padding-left: 1.5rem !important;
}

.mdxeditor-overrides .mdx-content ul {
  list-style: disc !important;
}

.mdxeditor-overrides .mdx-content ol {
  list-style: decimal !important;
}

.mdxeditor-overrides .mdx-content li {
  margin: 0.25rem 0 !important;
}

.mdxeditor-overrides .mdx-toolbar-row {
  gap: 1px;
}

.mdxeditor-overrides .mdx-inline-format-group [class*="_toolbarGroupOfGroups_"] {
  margin-inline: 0;
  gap: 1px;
}

/* MDXEditor's default active state is deliberately subtle. Make selected
   inline formatting unmistakable in the compact CTA toolbar. */
.mdxeditor-overrides [class*="_toolbarToggleItem_"][data-state="on"],
.mdxeditor-overrides [class*="_toolbarToggleItem_"][data-state="on"]:hover {
  background: #525252 !important;
  color: #fff !important;
}

.mdxeditor-overrides [class*="_toolbarToggleItem_"][data-state="on"] svg {
  color: currentColor !important;
}

/* No-link-leaf editors (Email/Phone/EmailLabel): allow bold/color but not link styling */
.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] .mdx-content {
  font-weight: 400 !important;
}

.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] .mdx-content a,
.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] a.mdxeditor-link,
.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] [data-lexical-link="true"] {
  font-weight: inherit !important;
  text-decoration: inherit !important;
}

/* Only bold if user explicitly applied strong */
.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] .mdx-content a strong,
.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] .mdx-content strong a {
  font-weight: 600 !important;
}

/* Prevent "inline code" styling around colored text or links */
.mdxeditor-overrides .mdx-content a code,
.mdxeditor-overrides .mdx-content code a,
.mdxeditor-overrides .mdx-content span[style] code,
.mdxeditor-overrides .mdx-content code span[style] {
  font-weight: inherit !important;
  font-style: normal !important;
  font-family: inherit !important;
  background: transparent !important;
  padding: 0 !important;
  border: 0 !important;
}
      `}</style>
        </div>
    );
}
